import { ReactNode } from 'react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { Box, Button, ButtonGroup, Link, Paper, Typography, makeStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';

/** 상태 배지 톤 — 디자인의 녹(일치·완료)/황(차단)/적(누락·실패)/청(진행)/회(제외)를 테마 팔레트로 옮긴 것 */
export type Tone = 'ok' | 'warn' | 'error' | 'info' | 'neutral' | 'solid';

const useStyles = makeStyles(theme => {
  const soft = (c: string, fg: string) => ({ background: alpha(c, 0.14), color: fg });
  return {
    pill: { display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 8, fontSize: 12, lineHeight: '18px', whiteSpace: 'nowrap', fontWeight: 500 },
    ok: soft(theme.palette.success.main, theme.palette.success.dark),
    warn: soft(theme.palette.warning.main, theme.palette.warning.dark),
    error: soft(theme.palette.error.main, theme.palette.error.dark),
    info: soft(theme.palette.primary.main, theme.palette.primary.dark),
    neutral: { background: theme.palette.action.selected, color: theme.palette.text.secondary },
    solid: { background: theme.palette.primary.main, color: theme.palette.primary.contrastText, fontWeight: 700 },
  };
});

export const Pill = ({ tone, children, title }: { tone: Tone; children: ReactNode; title?: string }) => {
  const c = useStyles();
  return <span className={`${c.pill} ${c[tone]}`} title={title}>{children}</span>;
};

/** 디자인의 세그먼트 필터·토글 (선택된 칸만 채움) */
export function Seg<T extends string>({ value, options, onChange, disabled, label }: {
  value: T; options: [T, string][]; onChange: (v: T) => void; disabled?: boolean; label?: string;
}) {
  return (
    <ButtonGroup size="small" color="primary" disabled={disabled} aria-label={label}>
      {options.map(([v, l]) => (
        <Button key={v} variant={v === value ? 'contained' : 'outlined'} aria-pressed={v === value} onClick={() => onChange(v)}>{l}</Button>
      ))}
    </ButtonGroup>
  );
}

export const TICKET = /^[A-Z]+-[0-9]+$/;

/** 신규 등록 화면 공용 섹션 카드 — 번호 원 + 제목 */
export const Step = ({ n, title, sub, children, action }: { n: number; title: string; sub?: string; children: ReactNode; action?: ReactNode }) => (
  <Paper variant="outlined" component="section" aria-label={`${n}단계 ${title}`}>
    <Box p={2.5} display="flex" flexDirection="column" style={{ gap: 14 }}>
      <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 10 }}>
        <Box
          width={24} height={24} borderRadius={12} bgcolor="primary.main" color="primary.contrastText"
          display="flex" alignItems="center" justifyContent="center" fontSize={13} fontWeight={700}
        >
          {n}
        </Box>
        <Typography variant="subtitle1">{title}</Typography>
        {sub && <Typography variant="caption" color="textSecondary">{sub}</Typography>}
        {action && <Box ml="auto">{action}</Box>}
      </Box>
      {children}
    </Box>
  </Paper>
);

/** `2026-10-19 14:02` */
export const fmtAt = (s: string) => new Date(s).toLocaleString('sv-SE').slice(0, 16);
/** `user:default/junghoo.park` → `junghoo.park` */
export const shortActor = (a: string) => a.replace(/^user:[^/]+\//, '');

/** app.jiraBaseUrl 이 있으면 새 탭 링크, 없으면 키만 */
export const TicketLink = ({ ticketKey, title }: { ticketKey: string; title?: string }) => {
  const base = useApi(configApiRef).getOptionalString('app.jiraBaseUrl');
  if (!base) return <span title={title}>{ticketKey}</span>;
  return (
    <Link href={`${base}/browse/${ticketKey}`} target="_blank" rel="noreferrer" title={title} onClick={e => e.stopPropagation()}>{ticketKey}</Link>
  );
};
